import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Style } from "@tecnobit-srl/styles-manager";
import "@tecnobit-srl/menu/css";
import { useAddRouterEventListener, usePage } from "@tecnobit-srl/router";
import { filter, isEmpty, last, map, some } from "lodash-es";
import useAnimation from "@tecnobit-srl/hook-use-animation";
import { Overlay, useBodyScrollLock } from "@tecnobit-srl/ui-overlay";
import { zIndexes } from "@tecnobit-srl/ui-zindexes";
import { useDebouncedResizeHandler } from "@tecnobit-srl/hook-use-window-width";
import { Content } from "@tecnobit-srl/content-types-store";
import { ThemeProvider } from "@tecnobit-srl/ui-theme";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import useClickOutside from "@tecnobit-srl/hook-use-click-outside";
import { FontAwesome } from "@tecnobit-srl/ui-font-awesome";
import { Link } from "@tecnobit-srl/content-types-link/lazy";
//#region ../styled-system/styled-system/helpers.js
function isObject(v) {
	return typeof v === "object" && v != null && !Array.isArray(v);
}
var HAS_OWN = Object.prototype.hasOwnProperty;
function isBaseCondition(v) {
	return v === "base";
}
function filterBaseConditions(c) {
	const out = [];
	for (let i = 0; i < c.length; i++) if (!isBaseCondition(c[i])) out.push(c[i]);
	return out;
}
function toHash(v) {
	let h = 5381;
	for (let i = v.length; i;) h = h * 33 ^ v.charCodeAt(--i);
	let x = h >>> 0, out = "";
	for (; x > 52; x = x / 52 | 0) {
		const c = x % 52;
		out = String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
	}
	const c = x % 52;
	return String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
}
function toCssVar(path) {
	return "var(--" + toHash(path.replaceAll(".", "-")) + ")";
}
function colorMix(tokens, path, resolveVar) {
	if (!path.startsWith("colors.")) return;
	const index = path.indexOf("/", 7);
	if (index === -1 || index === path.length - 1) return;
	const colorPath = path.slice(0, index);
	if (tokens[colorPath] === void 0) return;
	const rawOpacity = path.slice(index + 1);
	const opacity = tokens["opacity." + rawOpacity];
	const percent = opacity === void 0 ? Number(rawOpacity) : Number(opacity) * 100;
	if (Number.isNaN(percent)) return;
	return "color-mix(in oklab, " + (resolveVar || toCssVar)(colorPath) + " " + percent + "%, transparent)";
}
function toResponsiveObject(values, breakpoints) {
	const out = {};
	for (let i = 0; i < values.length; i++) if (values[i] != null) out[breakpoints[i]] = values[i];
	return out;
}
function walkObject(target, fn, options) {
	options ||= {};
	const { stop, getKey } = options;
	const inner = (value, path = []) => {
		if (!value || typeof value !== "object") return fn(value, path);
		if (stop?.(value, path)) return fn(value, path);
		const out = Array.isArray(value) ? [] : {};
		for (const prop in value) {
			if (!HAS_OWN.call(value, prop) || prop === "__proto__") continue;
			const key = getKey?.(prop, value[prop]) ?? prop;
			path.push(key);
			const next = inner(value[prop], path);
			path.pop();
			if (next != null) out[key] = next;
		}
		return out;
	};
	return inner(target);
}
function normalizeStyleObject(styles, context, shorthand) {
	const { utility, conditions } = context;
	const { hasShorthand, resolveShorthand } = utility;
	shorthand = shorthand !== false;
	return walkObject(styles, (value) => {
		if (Array.isArray(value)) return toResponsiveObject(value, conditions.breakpoints.keys);
		return value;
	}, {
		stop: Array.isArray,
		getKey: shorthand ? (prop) => hasShorthand ? resolveShorthand(prop) : prop : void 0
	});
}
function flatHashOrNull(args) {
	let h = 5381;
	for (let a = 0; a < args.length; a++) {
		const obj = args[a];
		if (obj === null || typeof obj !== "object") {
			h = h * 33 ^ 1;
			continue;
		}
		for (const k in obj) {
			const v = obj[k];
			const tv = typeof v;
			if (v !== null && tv === "object") return null;
			for (let i = 0; i < k.length; i++) h = h * 33 ^ k.charCodeAt(i);
			if (tv === "string") for (let i = 0; i < v.length; i++) h = h * 33 ^ v.charCodeAt(i);
			else if (tv === "number") h = h * 33 ^ (v | 0);
			else if (tv === "boolean") h = h * 33 ^ (v ? 991 : 997);
			else h = h * 33 ^ 2;
		}
	}
	return h >>> 0;
}
function flatArgsEqual(a, b) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const oa = a[i];
		const ob = b[i];
		if (oa === ob) continue;
		if (oa === null || ob === null || typeof oa !== "object" || typeof ob !== "object") return false;
		let n = 0;
		for (const k in oa) {
			if (oa[k] !== ob[k]) return false;
			n++;
		}
		if (n !== Object.keys(ob).length) return false;
	}
	return true;
}
function memo(fn) {
	const cache = /* @__PURE__ */ new Map();
	const stringCache = /* @__PURE__ */ new Map();
	const seen = /* @__PURE__ */ new WeakSet();
	const newNode = () => ({
		objects: /* @__PURE__ */ new WeakMap(),
		prims: /* @__PURE__ */ new Map(),
		out: void 0,
		has: false
	});
	const root = newNode();
	let lastHash;
	let lastKey;
	let lastValue;
	let hasLast = false;
	let misses = 0;
	const step = (node, v) => {
		if (v !== null && typeof v === "object") {
			let next = node.objects.get(v);
			if (next === void 0) {
				next = newNode();
				node.objects.set(v, next);
			}
			return next;
		}
		let next = node.prims.get(v);
		if (next === void 0) {
			if (node.prims.size > 64) node.prims.clear();
			next = newNode();
			node.prims.set(v, next);
		}
		return next;
	};
	const walk = (node, v) => {
		if (Array.isArray(v)) {
			node = step(node, "\0[");
			for (let i = 0; i < v.length; i++) node = walk(node, v[i]);
			return step(node, "\0]");
		}
		return step(node, v);
	};
	const readWalk = (node, v) => {
		if (node === void 0) return void 0;
		if (Array.isArray(v)) {
			node = node.prims.get("\0[");
			for (let i = 0; i < v.length && node !== void 0; i++) node = readWalk(node, v[i]);
			return node === void 0 ? void 0 : node.prims.get("\0]");
		}
		return v !== null && typeof v === "object" ? node.objects.get(v) : node.prims.get(v);
	};
	const markSeen = (v) => {
		if (Array.isArray(v)) {
			let all = true;
			for (let i = 0; i < v.length; i++) if (!markSeen(v[i])) all = false;
			return all;
		}
		if (v === null || typeof v !== "object") return true;
		if (seen.has(v)) return true;
		seen.add(v);
		return false;
	};
	return ((...args) => {
		let composed = false;
		for (let i = 0; i < args.length; i++) if (Array.isArray(args[i])) {
			composed = true;
			break;
		}
		if (composed) {
			let node = root;
			for (let i = 0; i < args.length && node !== void 0; i++) node = readWalk(node, args[i]);
			if (node !== void 0 && node.has) return node.out;
			const composedKey = JSON.stringify(args);
			let composedOut = hasLast && lastHash === void 0 && composedKey === lastKey ? lastValue : stringCache.get(composedKey);
			if (composedOut === void 0) {
				composedOut = fn(...args);
				stringCache.set(composedKey, composedOut);
				if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
			}
			let reused = false;
			if ((++misses & 3) === 0) {
				reused = true;
				for (let i = 0; i < args.length; i++) if (!markSeen(args[i])) reused = false;
			}
			if (reused) {
				let insert = root;
				for (let i = 0; i < args.length; i++) insert = walk(insert, args[i]);
				insert.out = composedOut;
				insert.has = true;
			}
			lastHash = void 0;
			lastKey = composedKey;
			lastValue = composedOut;
			hasLast = true;
			return composedOut;
		}
		const hash = flatHashOrNull(args);
		if (hash !== null) {
			if (hasLast && lastHash === hash && flatArgsEqual(args, lastKey)) return lastValue;
			let bucket = cache.get(hash);
			if (bucket) {
				for (let i = 0; i < bucket.length; i++) if (flatArgsEqual(args, bucket[i].args)) {
					lastHash = hash;
					lastKey = args;
					lastValue = bucket[i].out;
					hasLast = true;
					return bucket[i].out;
				}
			}
			const out = fn(...args);
			if (!bucket) {
				bucket = [];
				cache.set(hash, bucket);
			}
			bucket.push({
				args,
				out
			});
			if (bucket.length > 8) bucket.shift();
			if (cache.size > 500) cache.delete(cache.keys().next().value);
			lastHash = hash;
			lastKey = args;
			lastValue = out;
			hasLast = true;
			return out;
		}
		const key = JSON.stringify(args);
		if (hasLast && lastHash === void 0 && key === lastKey) return lastValue;
		const cached = stringCache.get(key);
		if (cached !== void 0) {
			lastHash = void 0;
			lastKey = key;
			lastValue = cached;
			hasLast = true;
			return cached;
		}
		const out = fn(...args);
		stringCache.set(key, out);
		if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
		lastHash = void 0;
		lastKey = key;
		lastValue = out;
		hasLast = true;
		return out;
	});
}
function weakMemo(fn) {
	const cache = /* @__PURE__ */ new WeakMap();
	return ((arg) => {
		if (!arg || typeof arg !== "object") return fn(arg);
		if (cache.has(arg)) return cache.get(arg);
		const out = fn(arg);
		cache.set(arg, out);
		return out;
	});
}
function mergeProps(...src) {
	const out = {};
	for (const obj of src) {
		if (!obj) continue;
		for (const k in obj) {
			if (!HAS_OWN.call(obj, k) || k === "__proto__" || k === "constructor" || k === "prototype") continue;
			const prev = out[k];
			const next = obj[k];
			out[k] = isObject(prev) && isObject(next) ? mergeProps(prev, next) : next;
		}
	}
	return out;
}
var WHITESPACE_REGEX = /[\n\s]+/g;
var sanitizeStyleValue = (value) => typeof value === "string" ? value.replace(WHITESPACE_REGEX, " ") : value;
function resolveStyleArgs(styles, context) {
	const out = [];
	const visit = (items) => {
		for (let i = 0; i < items.length; i++) {
			const style = items[i];
			if (Array.isArray(style)) {
				visit(style);
				continue;
			}
			if (!isObject(style)) continue;
			for (const key in style) if (style[key] !== void 0) {
				out.push(style);
				break;
			}
		}
	};
	visit(styles);
	if (out.length < 2) return out;
	for (let i = 0; i < out.length; i++) out[i] = normalizeStyleObject(out[i], context);
	return out;
}
function createSerializeCss(context) {
	const u = context.utility;
	const c = context.conditions;
	const hash = context.hash;
	const fmt = (s) => u.prefix ? u.prefix + "-" + s : s;
	const toClass = (paths, name) => {
		const parts = c.finalize(paths);
		parts.push(hash ? name : fmt(name));
		return hash ? fmt(u.toHash(parts, toHash)) : parts.join(":");
	};
	return weakMemo(memo(function serializeCss({ base, ...styles } = {}) {
		const obj = normalizeStyleObject(base ? Object.assign(styles, base) : styles, context);
		const set = /* @__PURE__ */ new Set();
		walkObject(obj, (value, paths) => {
			if (value == null) return;
			const important = isImportant(value);
			const [prop, ...all] = c.shift(paths);
			const cond = filterBaseConditions(all);
			const res = u.transform(prop, withoutSpace(withoutImportant(sanitizeStyleValue(value))));
			let name = toClass(cond, res.className);
			if (important) name += "!";
			set.add(name);
		});
		let out = "";
		for (const name of set) out += out ? " " + name : name;
		return out;
	}));
}
function createMergeCss(context) {
	return function mergeCss() {
		return mergeProps(...resolveStyleArgs(arguments, context));
	};
}
function createSerializeCssArgs(serializeCss, mergeCss) {
	return memo(function serializeCssArgs(...styles) {
		return serializeCss(mergeCss(...styles));
	});
}
var HYPHENATE_PROPERTY_REGEX = /[A-Z]/g;
var MS_PROPERTY_REGEX = /^ms-/;
function hypenateProperty(property) {
	return property.startsWith("--") ? property : property.replace(HYPHENATE_PROPERTY_REGEX, "-$&").replace(MS_PROPERTY_REGEX, "-ms-").toLowerCase();
}
function withoutSpace(str) {
	return typeof str === "string" && str.indexOf(" ") >= 0 ? str.replaceAll(" ", "_") : str;
}
var IMPORTANT_REGEX = /\s*!(important)?\s*$/i;
function isImportant(value) {
	return typeof value === "string" ? IMPORTANT_REGEX.test(value) : false;
}
function withoutImportant(value) {
	return typeof value === "string" ? value.replace(IMPORTANT_REGEX, "").trim() : value;
}
//#endregion
//#region ../styled-system/styled-system/css/conditions.js
var conditions = /* @__PURE__ */ new Set(/* @__PURE__ */ "2xl,2xlDown,2xlOnly,2xlToDesktop,2xlToTablet,2xlToXs,@/2xl,@/2xlDown,@/2xlOnly,@/2xlTo3xl,@/2xlTo4xl,@/2xlTo5xl,@/2xlTo6xl,@/2xlTo7xl,@/2xlTo8xl,@/2xs,@/2xsDown,@/2xsOnly,@/2xsTo2xl,@/2xsTo3xl,@/2xsTo4xl,@/2xsTo5xl,@/2xsTo6xl,@/2xsTo7xl,@/2xsTo8xl,@/2xsToLg,@/2xsToMd,@/2xsToSm,@/2xsToXl,@/2xsToXs,@/3xl,@/3xlDown,@/3xlOnly,@/3xlTo4xl,@/3xlTo5xl,@/3xlTo6xl,@/3xlTo7xl,@/3xlTo8xl,@/3xs,@/3xsDown,@/3xsOnly,@/3xsTo2xl,@/3xsTo2xs,@/3xsTo3xl,@/3xsTo4xl,@/3xsTo5xl,@/3xsTo6xl,@/3xsTo7xl,@/3xsTo8xl,@/3xsToLg,@/3xsToMd,@/3xsToSm,@/3xsToXl,@/3xsToXs,@/4xl,@/4xlDown,@/4xlOnly,@/4xlTo5xl,@/4xlTo6xl,@/4xlTo7xl,@/4xlTo8xl,@/5xl,@/5xlDown,@/5xlOnly,@/5xlTo6xl,@/5xlTo7xl,@/5xlTo8xl,@/6xl,@/6xlDown,@/6xlOnly,@/6xlTo7xl,@/6xlTo8xl,@/7xl,@/7xlDown,@/7xlOnly,@/7xlTo8xl,@/8xl,@/8xlDown,@/8xlOnly,@/lg,@/lgDown,@/lgOnly,@/lgTo2xl,@/lgTo3xl,@/lgTo4xl,@/lgTo5xl,@/lgTo6xl,@/lgTo7xl,@/lgTo8xl,@/lgToXl,@/md,@/mdDown,@/mdOnly,@/mdTo2xl,@/mdTo3xl,@/mdTo4xl,@/mdTo5xl,@/mdTo6xl,@/mdTo7xl,@/mdTo8xl,@/mdToLg,@/mdToXl,@/sm,@/smDown,@/smOnly,@/smTo2xl,@/smTo3xl,@/smTo4xl,@/smTo5xl,@/smTo6xl,@/smTo7xl,@/smTo8xl,@/smToLg,@/smToMd,@/smToXl,@/xl,@/xlDown,@/xlOnly,@/xlTo2xl,@/xlTo3xl,@/xlTo4xl,@/xlTo5xl,@/xlTo6xl,@/xlTo7xl,@/xlTo8xl,@/xs,@/xsDown,@/xsOnly,@/xsTo2xl,@/xsTo3xl,@/xsTo4xl,@/xsTo5xl,@/xsTo6xl,@/xsTo7xl,@/xsTo8xl,@/xsToLg,@/xsToMd,@/xsToSm,@/xsToXl,_active,_after,_anyPointerCoarse,_anyPointerFine,_anyPointerNone,_atValue,_autofill,_backdrop,_before,_checked,_closed,_complete,_current,_currentPage,_currentStep,_dark,_default,_disabled,_dragging,_empty,_enabled,_even,_expanded,_file,_first,_firstLetter,_firstLine,_firstOfType,_focus,_focusVisible,_focusWithin,_fullscreen,_grabbed,_groupActive,_groupChecked,_groupDisabled,_groupExpanded,_groupFocus,_groupFocusVisible,_groupFocusWithin,_groupHover,_groupInvalid,_hidden,_highContrast,_highlighted,_horizontal,_hover,_icon,_inRange,_incomplete,_indeterminate,_inert,_invalid,_invertedColors,_landscape,_last,_lastOfType,_lessContrast,_light,_loading,_ltr,_marker,_moreContrast,_motionReduce,_motionSafe,_noscript,_now,_odd,_only,_onlyOfType,_open,_optional,_osDark,_osLight,_outOfRange,_overValue,_peerActive,_peerChecked,_peerDisabled,_peerExpanded,_peerFocus,_peerFocusVisible,_peerFocusWithin,_peerHover,_peerInvalid,_peerPlaceholderShown,_placeholder,_placeholderShown,_pointerCoarse,_pointerFine,_pointerNone,_portrait,_pressed,_print,_rangeEnd,_rangeStart,_readOnly,_readWrite,_required,_rtl,_scrollbar,_scrollbarThumb,_scrollbarTrack,_selected,_selection,_starting,_target,_today,_topmost,_unavailable,_underValue,_userInvalid,_userValid,_valid,_vertical,_visited,base,desktop,desktopDown,desktopOnly,lg,lgDown,lgOnly,lgTo2xl,lgToDesktop,lgToTablet,lgToXl,lgToXs,md,mdDown,mdOnly,mdTo2xl,mdToDesktop,mdToLg,mdToTablet,mdToXl,mdToXs,sm,smDown,smOnly,smTo2xl,smToDesktop,smToLg,smToMd,smToTablet,smToXl,smToXs,tablet,tabletDown,tabletOnly,tabletToDesktop,xl,xlDown,xlOnly,xlTo2xl,xlToDesktop,xlToTablet,xlToXs,xs,xsDown,xsOnly,xsToDesktop,xsToTablet".split(","));
var conditionRe = /^@|&/;
var underscoreRe = /^_/;
var selectorRe = /&|@/;
var breakpointKeys = [
	"base",
	"sm",
	"md",
	"lg",
	"xl",
	"2xl",
	"xs",
	"tablet",
	"desktop"
];
function isCondition(v) {
	return conditions.has(v) || conditionRe.test(v);
}
function finalizeConditions(paths) {
	return paths.map((p) => {
		if (conditions.has(p)) return p.replace(underscoreRe, "");
		if (selectorRe.test(p)) return `[${withoutSpace(p.trim())}]`;
		return p;
	});
}
function sortConditions(paths) {
	return [...paths].sort((a, b) => {
		const aa = isCondition(a);
		const bb = isCondition(b);
		return aa && !bb ? 1 : !aa && bb ? -1 : 0;
	});
}
//#endregion
//#region ../styled-system/styled-system/css/css.js
var utilities = "WebkitTextFillColor:wktf-c,accentColor:ac-c,alignContent:ac,alignItems:ai,alignSelf:as,animation:anim,animationComposition:anim-comp,animationDelay:anim-dly,animationDirection:anim-dir,animationDuration:anim-dur,animationFillMode:anim-fm,animationIterationCount:anim-ic,animationName:anim-n,animationPlayState:anim-ps,animationRange:anim-r,animationRangeEnd:anim-re,animationRangeStart:anim-rs,animationState:anim-s,animationTimeline:anim-tl,animationTimingFunction:anim-tmf,appearance:ap,aspectRatio:asp,backdropBlur:bkdp-blur,backdropBrightness:bkdp-brightness,backdropContrast:bkdp-contrast,backdropFilter:bkdp,backdropGrayscale:bkdp-grayscale,backdropHueRotate:bkdp-hue-rotate,backdropInvert:bkdp-invert,backdropOpacity:bkdp-opacity,backdropSaturate:bkdp-saturate,backdropSepia:bkdp-sepia,backfaceVisibility:bfv,background:bg/1,backgroundAttachment:bg-a/bgAttachment,backgroundBlendMode:bg-bm/bgBlendMode,backgroundClip:bg-cp/bgClip,backgroundColor:bg-c/bgColor,backgroundConic:bg-conic/bgConic,backgroundGradient:bg-grad/bgGradient,backgroundImage:bg-i/bgImage,backgroundLinear:bg-linear/bgLinear,backgroundOrigin:bg-o/bgOrigin,backgroundPosition:bg-p/bgPosition,backgroundPositionX:bg-p-x/bgPositionX,backgroundPositionY:bg-p-y/bgPositionY,backgroundRadial:bg-radial/bgRadial,backgroundRepeat:bg-r/bgRepeat,backgroundSize:bg-s/bgSize,blockSize:h-bs,border:bd,borderBlock:bd-y/borderY,borderBlockColor:bd-y-c/borderYColor,borderBlockEnd:bd-be,borderBlockEndColor:bd-be-c,borderBlockEndWidth:bd-be-w,borderBlockStart:bd-bs,borderBlockStartColor:bd-bs-c,borderBlockStartWidth:bd-bs-w,borderBlockWidth:bd-y-w/borderYWidth,borderBottom:bd-b,borderBottomColor:bd-b-c,borderBottomLeftRadius:bdr-bl/roundedBottomLeft,borderBottomRadius:bdr-b/roundedBottom,borderBottomRightRadius:bdr-br/roundedBottomRight,borderBottomWidth:bd-b-w,borderCollapse:bd-cl,borderColor:bd-c,borderEndEndRadius:bdr-ee/roundedEndEnd,borderEndRadius:bdr-e/roundedEnd,borderEndStartRadius:bdr-es/roundedEndStart,borderInline:bd-x/borderX,borderInlineColor:bd-x-c/borderXColor,borderInlineEnd:bd-e/borderEnd,borderInlineEndColor:bd-e-c/borderEndColor,borderInlineEndWidth:bd-e-w/borderEndWidth,borderInlineStart:bd-s/borderStart,borderInlineStartColor:bd-s-c/borderStartColor,borderInlineStartWidth:bd-s-w/borderStartWidth,borderInlineWidth:bd-x-w/borderXWidth,borderLeft:bd-l,borderLeftColor:bd-l-c,borderLeftRadius:bdr-l/roundedLeft,borderLeftWidth:bd-l-w,borderRadius:bdr/rounded,borderRight:bd-r,borderRightColor:bd-r-c,borderRightRadius:bdr-r/roundedRight,borderRightWidth:bd-r-w,borderSpacing:bd-sp,borderSpacingX:bd-sx,borderSpacingY:bd-sy,borderStartEndRadius:bdr-se/roundedStartEnd,borderStartRadius:bdr-s/roundedStart,borderStartStartRadius:bdr-ss/roundedStartStart,borderTop:bd-t,borderTopColor:bd-t-c,borderTopLeftRadius:bdr-tl/roundedTopLeft,borderTopRadius:bdr-t/roundedTop,borderTopRightRadius:bdr-tr/roundedTopRight,borderTopWidth:bd-t-w,borderWidth:bd-w,boxDecorationBreak:bx-db,boxShadow:bx-sh/shadow,boxShadowColor:bx-sh-c/shadowColor,boxSize:size,boxSizing:bx-s,caretColor:ca-c,clipPath:cp-path,color:c,columnGap:cg,container:cq,containerName:cq-n,containerType:cq-t,display:d,divideColor:dvd-c,divideStyle:dvd-s,divideX:dvd-x,divideY:dvd-y,flexBasis:flex-b,flexDirection:flex-d/flexDir,flexGrow:flex-g,flexShrink:flex-sh,focusRingColor:focus-ring-c,focusRingOffset:focus-ring-o,focusRingStyle:focus-ring-s,focusRingWidth:focus-ring-w,focusVisibleRing:focus-v-ring,fontFamily:ff,fontFeatureSettings:ff-s,fontKerning:fk,fontPalette:fp,fontSize:fs,fontSizeAdjust:fs-a,fontSmoothing:fsmt,fontVariant:fv,fontVariantAlternates:fv-alt,fontVariantCaps:fv-caps,fontVariantNumeric:fv-num,fontVariationSettings:fv-s,fontWeight:fw,gradientFrom:grad-from,gradientFromPosition:grad-from-pos,gradientTo:grad-to,gradientToPosition:grad-to-pos,gradientVia:grad-via,gradientViaPosition:grad-via-pos,gridAutoColumns:grid-ac,gridAutoFlow:grid-af,gridAutoRows:grid-ar,gridColumn:grid-c,gridColumnEnd:grid-ce,gridColumnGap:grid-cg,gridColumnStart:grid-cs,gridGap:grid-g,gridRow:grid-r,gridRowGap:grid-rg,gridTemplateColumns:grid-tc,gridTemplateRows:grid-tr,height:h/1,hideBelow:show,hideFrom:hide,hyphens:hy,inlineSize:w-is,insetBlock:inset-y/insetY,insetBlockEnd:inset-be,insetBlockStart:inset-bs,insetInline:inset-x/insetX,insetInlineEnd:inset-e/end/insetEnd,insetInlineStart:inset-s/insetStart/start,justifyContent:jc,letterSpacing:ls,lineClamp:lc,lineHeight:lh,listStyle:li-s,listStyleImage:li-img,listStylePosition:li-pos,listStyleType:li-t,margin:m/1,marginBlock:my/marginY/1,marginBlockEnd:mbe,marginBlockStart:mbs,marginBottom:mb/1,marginInline:mx/marginX/1,marginInlineEnd:me/marginEnd/1,marginInlineStart:ms/marginStart/1,marginLeft:ml/1,marginRight:mr/1,marginTop:mt/1,mask:msk,maskBottomFrom:msk-b-from,maskBottomFromColor:msk-b-from-c,maskBottomTo:msk-b-to,maskBottomToColor:msk-b-to-c,maskClip:msk-cp,maskComposite:msk-cmp,maskConic:msk-conic,maskConicFrom:msk-conic-from,maskConicFromColor:msk-conic-from-c,maskConicTo:msk-conic-to,maskConicToColor:msk-conic-to-c,maskImage:msk-i,maskLeftFrom:msk-l-from,maskLeftFromColor:msk-l-from-c,maskLeftTo:msk-l-to,maskLeftToColor:msk-l-to-c,maskLinear:msk-linear,maskLinearFrom:msk-linear-from,maskLinearFromColor:msk-linear-from-c,maskLinearTo:msk-linear-to,maskLinearToColor:msk-linear-to-c,maskMode:msk-md,maskOrigin:msk-o,maskPosition:msk-p,maskRadial:msk-radial,maskRadialAt:msk-radial-at,maskRadialFrom:msk-radial-from,maskRadialFromColor:msk-radial-from-c,maskRadialShape:msk-radial-shape,maskRadialSize:msk-radial-sz,maskRadialTo:msk-radial-to,maskRadialToColor:msk-radial-to-c,maskRepeat:msk-r,maskRightFrom:msk-r-from,maskRightFromColor:msk-r-from-c,maskRightTo:msk-r-to,maskRightToColor:msk-r-to-c,maskSize:msk-s,maskTopFrom:msk-t-from,maskTopFromColor:msk-t-from-c,maskTopTo:msk-t-to,maskTopToColor:msk-t-to-c,maskType:msk-t,maskXFrom:msk-x-from,maskXFromColor:msk-x-from-c,maskXTo:msk-x-to,maskXToColor:msk-x-to-c,maskYFrom:msk-y-from,maskYFromColor:msk-y-from-c,maskYTo:msk-y-to,maskYToColor:msk-y-to-c,maxBlockSize:max-b,maxHeight:max-h/maxH,maxInlineSize:max-w-is,maxWidth:max-w/maxW,minBlockSize:min-h-bs,minHeight:min-h/minH,minInlineSize:min-w-is,minWidth:min-w/minW,mixBlendMode:mix-bm,objectFit:obj-f,objectPosition:obj-p,opacity:op,outline:ring/1,outlineColor:ring-c/ringColor,outlineOffset:ring-o/ringOffset,outlineWidth:ring-w/ringWidth,overflow:ov,overflowAnchor:ov-a,overflowBlock:ov-b,overflowClipBox:ovcp-bx,overflowClipMargin:ovcp-m,overflowInline:ov-i,overflowWrap:ov-wrap,overflowX:ov-x,overflowY:ov-y,overscrollBehavior:ovs-b,overscrollBehaviorBlock:ovs-bb,overscrollBehaviorInline:ovs-bi,overscrollBehaviorX:ovs-bx,overscrollBehaviorY:ovs-by,padding:p/1,paddingBlock:py/paddingY/1,paddingBlockEnd:pbe,paddingBlockStart:pbs,paddingBottom:pb/1,paddingInline:px/paddingX/1,paddingInlineEnd:pe/paddingEnd/1,paddingInlineStart:ps/paddingStart/1,paddingLeft:pl/1,paddingRight:pr/1,paddingTop:pt/1,position:pos/1,rowGap:rg,scrollBehavior:scr-bhv,scrollMargin:scr-m,scrollMarginBlock:scr-my/scrollMarginY,scrollMarginBlockEnd:scr-mbe,scrollMarginBlockStart:scr-mbt,scrollMarginBottom:scr-mb,scrollMarginInline:scr-mx/scrollMarginX,scrollMarginInlineEnd:scr-me,scrollMarginInlineStart:scr-ms,scrollMarginLeft:scr-ml,scrollMarginRight:scr-mr,scrollMarginTop:scr-mt,scrollPadding:scr-p,scrollPaddingBlock:scr-py/scrollPaddingY,scrollPaddingBlockEnd:scr-pbe,scrollPaddingBlockStart:scr-pbs,scrollPaddingBottom:scr-pb,scrollPaddingInline:scr-px/scrollPaddingX,scrollPaddingInlineEnd:scr-pe,scrollPaddingInlineStart:scr-ps,scrollPaddingLeft:scr-pl,scrollPaddingRight:scr-pr,scrollPaddingTop:scr-pt,scrollSnapAlign:scr-sa,scrollSnapCoordinate:scrs-c,scrollSnapDestination:scrs-d,scrollSnapMargin:scrs-m,scrollSnapMarginBottom:scrs-mb,scrollSnapMarginLeft:scrs-ml,scrollSnapMarginRight:scrs-mr,scrollSnapMarginTop:scrs-mt,scrollSnapPointsX:scrs-px,scrollSnapPointsY:scrs-py,scrollSnapStop:scrs-s,scrollSnapStrictness:scrs-strt,scrollSnapType:scrs-t,scrollSnapTypeX:scrs-tx,scrollSnapTypeY:scrs-ty,scrollTimeline:scrtl,scrollTimelineAxis:scrtl-a,scrollTimelineName:scrtl-n,scrollbar:scr-bar,scrollbarColor:scr-bar-c,scrollbarGutter:scr-bar-g,scrollbarThumb:scr-bar-th,scrollbarTrack:scr-bar-tk,scrollbarWidth:scr-bar-w,spaceX:sx,spaceY:sy,srOnly:sr,stroke:stk,strokeDasharray:stk-dsh,strokeDashoffset:stk-do,strokeLinecap:stk-lc,strokeLinejoin:stk-lj,strokeMiterlimit:stk-ml,strokeOpacity:stk-op,strokeWidth:stk-w,tableLayout:tbl,textAlign:ta,textDecoration:td,textDecorationColor:td-c,textDecorationStyle:td-s,textDecorationThickness:td-t,textEmphasisColor:te-c,textGradient:txt-grad,textIndent:ti,textOverflow:tov,textShadow:tsh,textShadowColor:tsh-c/textShadowColor,textSizeAdjust:txt-adj,textStyle:textStyle,textTransform:tt,textUnderlineOffset:tu-o,textWrap:tw,touchAction:tch-a,transform:trf,transformBox:trf-b,transformOrigin:trf-o,transformStyle:trf-s,transition:trs,transitionDelay:trs-dly,transitionDuration:trs-dur,transitionProperty:trs-prop,transitionTimingFunction:trs-tmf,translateX:/x,translateY:/y,translateZ:/z,truncate:trunc,userSelect:us,verticalAlign:va,visibility:vis,width:w/1,wordBreak:wb,zIndex:z";
function createCssContext() {
	const classNameByProp = /* @__PURE__ */ new Map();
	const shorthands = /* @__PURE__ */ new Map();
	utilities.split(",").forEach((utility) => {
		const [prop, meta] = utility.split(":");
		const [className, ...shorthandList] = meta.split("/");
		if (className) classNameByProp.set(prop, className);
		shorthandList.forEach((shorthand) => {
			const key = shorthand === "1" ? className : shorthand;
			shorthands.set(key, prop);
		});
	});
	const resolveShorthand = (prop) => shorthands.get(prop) || prop;
	return {
		hash: true,
		conditions: {
			shift: sortConditions,
			finalize: finalizeConditions,
			breakpoints: { keys: breakpointKeys }
		},
		utility: {
			prefix: null,
			hasShorthand: true,
			toHash(path, hashFn) {
				return hashFn(path.join(":"));
			},
			transform(prop, value) {
				const key = resolveShorthand(prop);
				return { className: `${classNameByProp.get(key) || hypenateProperty(key)}_${withoutSpace(value)}` };
			},
			resolveShorthand
		}
	};
}
var cssContext = /* @__PURE__ */ createCssContext();
var serializeCss = /* @__PURE__ */ createSerializeCss(cssContext);
var mergeCss = /* @__PURE__ */ createMergeCss(cssContext);
var serializeCssArgs = /* @__PURE__ */ createSerializeCssArgs(serializeCss, mergeCss);
var css = /* @__PURE__ */ Object.assign(function css(...styles) {
	if (styles.length === 1 && isObject(styles[0])) return serializeCss(styles[0]);
	return serializeCssArgs(...styles);
}, { raw: function cssRaw(...styles) {
	return mergeCss(...styles);
} });
//#endregion
//#region ../styled-system/styled-system/css/cx.js
/**
* Conditionally join classNames into a single string
*/
function cx(...args) {
	let str = "", i = 0, arg;
	for (; i < arguments.length;) if ((arg = arguments[i++]) && typeof arg === "string") {
		str && (str += " ");
		str += arg;
	}
	return str;
}
//#endregion
//#region ../styled-system/styled-system/tokens/index.js
var tokens = {
	"animations.bounce": "bounce 1s infinite",
	"animations.ping": "ping 1s cubic-bezier(0, 0, 0.2, 1) infinite",
	"animations.pulse": "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
	"animations.spin": "spin 1s linear infinite",
	"aspectRatios.golden": "1.618 / 1",
	"aspectRatios.landscape": "4 / 3",
	"aspectRatios.portrait": "3 / 4",
	"aspectRatios.square": "1 / 1",
	"aspectRatios.ultrawide": "18 / 5",
	"aspectRatios.video": "16 / 9",
	"aspectRatios.wide": "16 / 9",
	"blurs.2xl": "40px",
	"blurs.3xl": "64px",
	"blurs.lg": "16px",
	"blurs.md": "12px",
	"blurs.sm": "8px",
	"blurs.xl": "24px",
	"blurs.xs": "4px",
	"borders.none": "none",
	"breakpoints.2xl": "96rem",
	"breakpoints.desktop": "1024px",
	"breakpoints.lg": "64rem",
	"breakpoints.md": "48rem",
	"breakpoints.sm": "40rem",
	"breakpoints.tablet": "768px",
	"breakpoints.xl": "80rem",
	"breakpoints.xs": "300px",
	"colors.accent.100": "var(--accent-100)",
	"colors.accent.200": "var(--accent-200)",
	"colors.accent.300": "var(--accent-300)",
	"colors.accent.400": "var(--accent-400)",
	"colors.accent.50": "var(--accent-50)",
	"colors.accent.500": "var(--accent-500)",
	"colors.accent.600": "var(--accent-600)",
	"colors.accent.700": "var(--accent-700)",
	"colors.accent.800": "var(--accent-800)",
	"colors.accent.900": "var(--accent-900)",
	"colors.accent.950": "var(--accent-950)",
	"colors.amber.100": "oklch(96.2% 0.059 95.617)",
	"colors.amber.200": "oklch(92.4% 0.12 95.746)",
	"colors.amber.300": "oklch(87.9% 0.169 91.605)",
	"colors.amber.400": "oklch(82.8% 0.189 84.429)",
	"colors.amber.50": "oklch(98.7% 0.022 95.277)",
	"colors.amber.500": "oklch(76.9% 0.188 70.08)",
	"colors.amber.600": "oklch(66.6% 0.179 58.318)",
	"colors.amber.700": "oklch(55.5% 0.163 48.998)",
	"colors.amber.800": "oklch(47.3% 0.137 46.201)",
	"colors.amber.900": "oklch(41.4% 0.112 45.904)",
	"colors.amber.950": "oklch(27.9% 0.077 45.635)",
	"colors.background": "var(--background)",
	"colors.black": "#000",
	"colors.blue.100": "oklch(93.2% 0.032 255.585)",
	"colors.blue.200": "oklch(88.2% 0.059 254.128)",
	"colors.blue.300": "oklch(80.9% 0.105 251.813)",
	"colors.blue.400": "oklch(70.7% 0.165 254.624)",
	"colors.blue.50": "oklch(97% 0.014 254.604)",
	"colors.blue.500": "oklch(62.3% 0.214 259.815)",
	"colors.blue.600": "oklch(54.6% 0.245 262.881)",
	"colors.blue.700": "oklch(48.8% 0.243 264.376)",
	"colors.blue.800": "oklch(42.4% 0.199 265.638)",
	"colors.blue.900": "oklch(37.9% 0.146 265.522)",
	"colors.blue.950": "oklch(28.2% 0.091 267.935)",
	"colors.colorPalette": "",
	"colors.colorPalette.100": "",
	"colors.colorPalette.200": "",
	"colors.colorPalette.300": "",
	"colors.colorPalette.400": "",
	"colors.colorPalette.50": "",
	"colors.colorPalette.500": "",
	"colors.colorPalette.600": "",
	"colors.colorPalette.700": "",
	"colors.colorPalette.800": "",
	"colors.colorPalette.900": "",
	"colors.colorPalette.950": "",
	"colors.current": "currentcolor",
	"colors.cyan.100": "oklch(95.6% 0.045 203.388)",
	"colors.cyan.200": "oklch(91.7% 0.08 205.041)",
	"colors.cyan.300": "oklch(86.5% 0.127 207.078)",
	"colors.cyan.400": "oklch(78.9% 0.154 211.53)",
	"colors.cyan.50": "oklch(98.4% 0.019 200.873)",
	"colors.cyan.500": "oklch(71.5% 0.143 215.221)",
	"colors.cyan.600": "oklch(60.9% 0.126 221.723)",
	"colors.cyan.700": "oklch(52% 0.105 223.128)",
	"colors.cyan.800": "oklch(45% 0.085 224.283)",
	"colors.cyan.900": "oklch(39.8% 0.07 227.392)",
	"colors.cyan.950": "oklch(30.2% 0.056 229.695)",
	"colors.emerald.100": "oklch(95% 0.052 163.051)",
	"colors.emerald.200": "oklch(90.5% 0.093 164.15)",
	"colors.emerald.300": "oklch(84.5% 0.143 164.978)",
	"colors.emerald.400": "oklch(76.5% 0.177 163.223)",
	"colors.emerald.50": "oklch(97.9% 0.021 166.113)",
	"colors.emerald.500": "oklch(69.6% 0.17 162.48)",
	"colors.emerald.600": "oklch(59.6% 0.145 163.225)",
	"colors.emerald.700": "oklch(50.8% 0.118 165.612)",
	"colors.emerald.800": "oklch(43.2% 0.095 166.913)",
	"colors.emerald.900": "oklch(37.8% 0.077 168.94)",
	"colors.emerald.950": "oklch(26.2% 0.051 172.552)",
	"colors.error.300": "var(--error-300)",
	"colors.error.500": "var(--error-500)",
	"colors.error.700": "var(--error-700)",
	"colors.fuchsia.100": "oklch(95.2% 0.037 318.852)",
	"colors.fuchsia.200": "oklch(90.3% 0.076 319.62)",
	"colors.fuchsia.300": "oklch(83.3% 0.145 321.434)",
	"colors.fuchsia.400": "oklch(74% 0.238 322.16)",
	"colors.fuchsia.50": "oklch(97.7% 0.017 320.058)",
	"colors.fuchsia.500": "oklch(66.7% 0.295 322.15)",
	"colors.fuchsia.600": "oklch(59.1% 0.293 322.896)",
	"colors.fuchsia.700": "oklch(51.8% 0.253 323.949)",
	"colors.fuchsia.800": "oklch(45.2% 0.211 324.591)",
	"colors.fuchsia.900": "oklch(40.1% 0.17 325.612)",
	"colors.fuchsia.950": "oklch(29.3% 0.136 325.661)",
	"colors.gray.100": "var(--grey-100)",
	"colors.gray.200": "var(--grey-200)",
	"colors.gray.300": "var(--grey-300)",
	"colors.gray.400": "var(--grey-400)",
	"colors.gray.50": "var(--grey-50)",
	"colors.gray.500": "var(--grey-500)",
	"colors.gray.600": "var(--grey-600)",
	"colors.gray.700": "var(--grey-700)",
	"colors.gray.800": "var(--grey-800)",
	"colors.gray.900": "var(--grey-900)",
	"colors.gray.950": "var(--grey-950)",
	"colors.green.100": "oklch(96.2% 0.044 156.743)",
	"colors.green.200": "oklch(92.5% 0.084 155.995)",
	"colors.green.300": "oklch(87.1% 0.15 154.449)",
	"colors.green.400": "oklch(79.2% 0.209 151.711)",
	"colors.green.50": "oklch(98.2% 0.018 155.826)",
	"colors.green.500": "oklch(72.3% 0.219 149.579)",
	"colors.green.600": "oklch(62.7% 0.194 149.214)",
	"colors.green.700": "oklch(52.7% 0.154 150.069)",
	"colors.green.800": "oklch(44.8% 0.119 151.328)",
	"colors.green.900": "oklch(39.3% 0.095 152.535)",
	"colors.green.950": "oklch(26.6% 0.065 152.934)",
	"colors.grey.100": "var(--grey-100)",
	"colors.grey.200": "var(--grey-200)",
	"colors.grey.300": "var(--grey-300)",
	"colors.grey.400": "var(--grey-400)",
	"colors.grey.50": "var(--grey-50)",
	"colors.grey.500": "var(--grey-500)",
	"colors.grey.600": "var(--grey-600)",
	"colors.grey.700": "var(--grey-700)",
	"colors.grey.800": "var(--grey-800)",
	"colors.grey.900": "var(--grey-900)",
	"colors.grey.950": "var(--grey-950)",
	"colors.indigo.100": "oklch(93% 0.034 272.788)",
	"colors.indigo.200": "oklch(87% 0.065 274.039)",
	"colors.indigo.300": "oklch(78.5% 0.115 274.713)",
	"colors.indigo.400": "oklch(67.3% 0.182 276.935)",
	"colors.indigo.50": "oklch(96.2% 0.018 272.314)",
	"colors.indigo.500": "oklch(58.5% 0.233 277.117)",
	"colors.indigo.600": "oklch(51.1% 0.262 276.966)",
	"colors.indigo.700": "oklch(45.7% 0.24 277.023)",
	"colors.indigo.800": "oklch(39.8% 0.195 277.366)",
	"colors.indigo.900": "oklch(35.9% 0.144 278.697)",
	"colors.indigo.950": "oklch(25.7% 0.09 281.288)",
	"colors.info.300": "var(--info-300)",
	"colors.info.500": "var(--info-500)",
	"colors.info.700": "var(--info-700)",
	"colors.lime.100": "oklch(96.7% 0.067 122.328)",
	"colors.lime.200": "oklch(93.8% 0.127 124.321)",
	"colors.lime.300": "oklch(89.7% 0.196 126.665)",
	"colors.lime.400": "oklch(84.1% 0.238 128.85)",
	"colors.lime.50": "oklch(98.6% 0.031 120.757)",
	"colors.lime.500": "oklch(76.8% 0.233 130.85)",
	"colors.lime.600": "oklch(64.8% 0.2 131.684)",
	"colors.lime.700": "oklch(53.2% 0.157 131.589)",
	"colors.lime.800": "oklch(45.3% 0.124 130.933)",
	"colors.lime.900": "oklch(40.5% 0.101 131.063)",
	"colors.lime.950": "oklch(27.4% 0.072 132.109)",
	"colors.mauve.100": "oklch(96% 0.003 325.6)",
	"colors.mauve.200": "oklch(92.2% 0.005 325.62)",
	"colors.mauve.300": "oklch(86.5% 0.012 325.68)",
	"colors.mauve.400": "oklch(71.1% 0.019 323.02)",
	"colors.mauve.50": "oklch(98.5% 0 0)",
	"colors.mauve.500": "oklch(54.2% 0.034 322.5)",
	"colors.mauve.600": "oklch(43.5% 0.029 321.78)",
	"colors.mauve.700": "oklch(36.4% 0.029 323.89)",
	"colors.mauve.800": "oklch(26.3% 0.024 320.12)",
	"colors.mauve.900": "oklch(21.2% 0.019 322.12)",
	"colors.mauve.950": "oklch(14.5% 0.008 326)",
	"colors.mist.100": "oklch(96.3% 0.002 197.1)",
	"colors.mist.200": "oklch(92.5% 0.005 214.3)",
	"colors.mist.300": "oklch(87.2% 0.007 219.6)",
	"colors.mist.400": "oklch(72.3% 0.014 214.4)",
	"colors.mist.50": "oklch(98.7% 0.002 197.1)",
	"colors.mist.500": "oklch(56% 0.021 213.5)",
	"colors.mist.600": "oklch(45% 0.017 213.2)",
	"colors.mist.700": "oklch(37.8% 0.015 216)",
	"colors.mist.800": "oklch(27.5% 0.011 216.9)",
	"colors.mist.900": "oklch(21.8% 0.008 223.9)",
	"colors.mist.950": "oklch(14.8% 0.004 228.8)",
	"colors.neutral.100": "oklch(97% 0 0)",
	"colors.neutral.200": "oklch(92.2% 0 0)",
	"colors.neutral.300": "oklch(87% 0 0)",
	"colors.neutral.400": "oklch(70.8% 0 0)",
	"colors.neutral.50": "oklch(98.5% 0 0)",
	"colors.neutral.500": "oklch(55.6% 0 0)",
	"colors.neutral.600": "oklch(43.9% 0 0)",
	"colors.neutral.700": "oklch(37.1% 0 0)",
	"colors.neutral.800": "oklch(26.9% 0 0)",
	"colors.neutral.900": "oklch(20.5% 0 0)",
	"colors.neutral.950": "oklch(14.5% 0 0)",
	"colors.olive.100": "oklch(96.6% 0.005 106.5)",
	"colors.olive.200": "oklch(93% 0.007 106.5)",
	"colors.olive.300": "oklch(88% 0.011 106.6)",
	"colors.olive.400": "oklch(73.7% 0.021 106.9)",
	"colors.olive.50": "oklch(98.8% 0.003 106.5)",
	"colors.olive.500": "oklch(58% 0.031 107.3)",
	"colors.olive.600": "oklch(46.6% 0.025 107.3)",
	"colors.olive.700": "oklch(39.4% 0.023 107.4)",
	"colors.olive.800": "oklch(28.6% 0.016 107.4)",
	"colors.olive.900": "oklch(22.8% 0.013 107.4)",
	"colors.olive.950": "oklch(15.3% 0.006 107.1)",
	"colors.orange.100": "oklch(95.4% 0.038 75.164)",
	"colors.orange.200": "oklch(90.1% 0.076 70.697)",
	"colors.orange.300": "oklch(83.7% 0.128 66.29)",
	"colors.orange.400": "oklch(75% 0.183 55.934)",
	"colors.orange.50": "oklch(98% 0.016 73.684)",
	"colors.orange.500": "oklch(70.5% 0.213 47.604)",
	"colors.orange.600": "oklch(64.6% 0.222 41.116)",
	"colors.orange.700": "oklch(55.3% 0.195 38.402)",
	"colors.orange.800": "oklch(47% 0.157 37.304)",
	"colors.orange.900": "oklch(40.8% 0.123 38.172)",
	"colors.orange.950": "oklch(26.6% 0.079 36.259)",
	"colors.overAccent.100": "var(--over-accent-100)",
	"colors.overAccent.200": "var(--over-accent-200)",
	"colors.overAccent.300": "var(--over-accent-300)",
	"colors.overAccent.400": "var(--over-accent-400)",
	"colors.overAccent.50": "var(--over-accent-50)",
	"colors.overAccent.500": "var(--over-accent-500)",
	"colors.overAccent.600": "var(--over-accent-600)",
	"colors.overAccent.700": "var(--over-accent-700)",
	"colors.overAccent.800": "var(--over-accent-800)",
	"colors.overAccent.900": "var(--over-accent-900)",
	"colors.overAccent.950": "var(--over-accent-950)",
	"colors.overSecondary.100": "var(--over-secondary-100)",
	"colors.overSecondary.200": "var(--over-secondary-200)",
	"colors.overSecondary.300": "var(--over-secondary-300)",
	"colors.overSecondary.400": "var(--over-secondary-400)",
	"colors.overSecondary.50": "var(--over-secondary-50)",
	"colors.overSecondary.500": "var(--over-secondary-500)",
	"colors.overSecondary.600": "var(--over-secondary-600)",
	"colors.overSecondary.700": "var(--over-secondary-700)",
	"colors.overSecondary.800": "var(--over-secondary-800)",
	"colors.overSecondary.900": "var(--over-secondary-900)",
	"colors.overSecondary.950": "var(--over-secondary-950)",
	"colors.pink.100": "oklch(94.8% 0.028 342.258)",
	"colors.pink.200": "oklch(89.9% 0.061 343.231)",
	"colors.pink.300": "oklch(82.3% 0.12 346.018)",
	"colors.pink.400": "oklch(71.8% 0.202 349.761)",
	"colors.pink.50": "oklch(97.1% 0.014 343.198)",
	"colors.pink.500": "oklch(65.6% 0.241 354.308)",
	"colors.pink.600": "oklch(59.2% 0.249 0.584)",
	"colors.pink.700": "oklch(52.5% 0.223 3.958)",
	"colors.pink.800": "oklch(45.9% 0.187 3.815)",
	"colors.pink.900": "oklch(40.8% 0.153 2.432)",
	"colors.pink.950": "oklch(28.4% 0.109 3.907)",
	"colors.purple.100": "oklch(94.6% 0.033 307.174)",
	"colors.purple.200": "oklch(90.2% 0.063 306.703)",
	"colors.purple.300": "oklch(82.7% 0.119 306.383)",
	"colors.purple.400": "oklch(71.4% 0.203 305.504)",
	"colors.purple.50": "oklch(97.7% 0.014 308.299)",
	"colors.purple.500": "oklch(62.7% 0.265 303.9)",
	"colors.purple.600": "oklch(55.8% 0.288 302.321)",
	"colors.purple.700": "oklch(49.6% 0.265 301.924)",
	"colors.purple.800": "oklch(43.8% 0.218 303.724)",
	"colors.purple.900": "oklch(38.1% 0.176 304.987)",
	"colors.purple.950": "oklch(29.1% 0.149 302.717)",
	"colors.red.100": "oklch(93.6% 0.032 17.717)",
	"colors.red.200": "oklch(88.5% 0.062 18.334)",
	"colors.red.300": "oklch(80.8% 0.114 19.571)",
	"colors.red.400": "oklch(70.4% 0.191 22.216)",
	"colors.red.50": "oklch(97.1% 0.013 17.38)",
	"colors.red.500": "oklch(63.7% 0.237 25.331)",
	"colors.red.600": "oklch(57.7% 0.245 27.325)",
	"colors.red.700": "oklch(50.5% 0.213 27.518)",
	"colors.red.800": "oklch(44.4% 0.177 26.899)",
	"colors.red.900": "oklch(39.6% 0.141 25.723)",
	"colors.red.950": "oklch(25.8% 0.092 26.042)",
	"colors.rose.100": "oklch(94.1% 0.03 12.58)",
	"colors.rose.200": "oklch(89.2% 0.058 10.001)",
	"colors.rose.300": "oklch(81% 0.117 11.638)",
	"colors.rose.400": "oklch(71.2% 0.194 13.428)",
	"colors.rose.50": "oklch(96.9% 0.015 12.422)",
	"colors.rose.500": "oklch(64.5% 0.246 16.439)",
	"colors.rose.600": "oklch(58.6% 0.253 17.585)",
	"colors.rose.700": "oklch(51.4% 0.222 16.935)",
	"colors.rose.800": "oklch(45.5% 0.188 13.697)",
	"colors.rose.900": "oklch(41% 0.159 10.272)",
	"colors.rose.950": "oklch(27.1% 0.105 12.094)",
	"colors.secondary.100": "var(--secondary-100)",
	"colors.secondary.200": "var(--secondary-200)",
	"colors.secondary.300": "var(--secondary-300)",
	"colors.secondary.400": "var(--secondary-400)",
	"colors.secondary.50": "var(--secondary-50)",
	"colors.secondary.500": "var(--secondary-500)",
	"colors.secondary.600": "var(--secondary-600)",
	"colors.secondary.700": "var(--secondary-700)",
	"colors.secondary.800": "var(--secondary-800)",
	"colors.secondary.900": "var(--secondary-900)",
	"colors.secondary.950": "var(--secondary-950)",
	"colors.sky.100": "oklch(95.1% 0.026 236.824)",
	"colors.sky.200": "oklch(90.1% 0.058 230.902)",
	"colors.sky.300": "oklch(82.8% 0.111 230.318)",
	"colors.sky.400": "oklch(74.6% 0.16 232.661)",
	"colors.sky.50": "oklch(97.7% 0.013 236.62)",
	"colors.sky.500": "oklch(68.5% 0.169 237.323)",
	"colors.sky.600": "oklch(58.8% 0.158 241.966)",
	"colors.sky.700": "oklch(50% 0.134 242.749)",
	"colors.sky.800": "oklch(44.3% 0.11 240.79)",
	"colors.sky.900": "oklch(39.1% 0.09 240.876)",
	"colors.sky.950": "oklch(29.3% 0.066 243.157)",
	"colors.slate.100": "oklch(96.8% 0.007 247.896)",
	"colors.slate.200": "oklch(92.9% 0.013 255.508)",
	"colors.slate.300": "oklch(86.9% 0.022 252.894)",
	"colors.slate.400": "oklch(70.4% 0.04 256.788)",
	"colors.slate.50": "oklch(98.4% 0.003 247.858)",
	"colors.slate.500": "oklch(55.4% 0.046 257.417)",
	"colors.slate.600": "oklch(44.6% 0.043 257.281)",
	"colors.slate.700": "oklch(37.2% 0.044 257.287)",
	"colors.slate.800": "oklch(27.9% 0.041 260.031)",
	"colors.slate.900": "oklch(20.8% 0.042 265.755)",
	"colors.slate.950": "oklch(12.9% 0.042 264.695)",
	"colors.stone.100": "oklch(97% 0.001 106.424)",
	"colors.stone.200": "oklch(92.3% 0.003 48.717)",
	"colors.stone.300": "oklch(86.9% 0.005 56.366)",
	"colors.stone.400": "oklch(70.9% 0.01 56.259)",
	"colors.stone.50": "oklch(98.5% 0.001 106.423)",
	"colors.stone.500": "oklch(55.3% 0.013 58.071)",
	"colors.stone.600": "oklch(44.4% 0.011 73.639)",
	"colors.stone.700": "oklch(37.4% 0.01 67.558)",
	"colors.stone.800": "oklch(26.8% 0.007 34.298)",
	"colors.stone.900": "oklch(21.6% 0.006 56.043)",
	"colors.stone.950": "oklch(14.7% 0.004 49.25)",
	"colors.taupe.100": "oklch(96% 0.002 17.2)",
	"colors.taupe.200": "oklch(92.2% 0.005 34.3)",
	"colors.taupe.300": "oklch(86.8% 0.007 39.5)",
	"colors.taupe.400": "oklch(71.4% 0.014 41.2)",
	"colors.taupe.50": "oklch(98.6% 0.002 67.8)",
	"colors.taupe.500": "oklch(54.7% 0.021 43.1)",
	"colors.taupe.600": "oklch(43.8% 0.017 39.3)",
	"colors.taupe.700": "oklch(36.7% 0.016 35.7)",
	"colors.taupe.800": "oklch(26.8% 0.011 36.5)",
	"colors.taupe.900": "oklch(21.4% 0.009 43.1)",
	"colors.taupe.950": "oklch(14.7% 0.004 49.3)",
	"colors.teal.100": "oklch(95.3% 0.051 180.801)",
	"colors.teal.200": "oklch(91% 0.096 180.426)",
	"colors.teal.300": "oklch(85.5% 0.138 181.071)",
	"colors.teal.400": "oklch(77.7% 0.152 181.912)",
	"colors.teal.50": "oklch(98.4% 0.014 180.72)",
	"colors.teal.500": "oklch(70.4% 0.14 182.503)",
	"colors.teal.600": "oklch(60% 0.118 184.704)",
	"colors.teal.700": "oklch(51.1% 0.096 186.391)",
	"colors.teal.800": "oklch(43.7% 0.078 188.216)",
	"colors.teal.900": "oklch(38.6% 0.063 188.416)",
	"colors.teal.950": "oklch(27.7% 0.046 192.524)",
	"colors.text.200": "var(--text-200)",
	"colors.text.300": "var(--text-300)",
	"colors.text.500": "var(--text-500)",
	"colors.text.700": "var(--text-700)",
	"colors.transparent": "oklch(0 0 0 / 0)",
	"colors.valid.300": "var(--valid-300)",
	"colors.valid.500": "var(--valid-500)",
	"colors.valid.700": "var(--valid-700)",
	"colors.violet.100": "oklch(94.3% 0.029 294.588)",
	"colors.violet.200": "oklch(89.4% 0.057 293.283)",
	"colors.violet.300": "oklch(81.1% 0.111 293.571)",
	"colors.violet.400": "oklch(70.2% 0.183 293.541)",
	"colors.violet.50": "oklch(96.9% 0.016 293.756)",
	"colors.violet.500": "oklch(60.6% 0.25 292.717)",
	"colors.violet.600": "oklch(54.1% 0.281 293.009)",
	"colors.violet.700": "oklch(49.1% 0.27 292.581)",
	"colors.violet.800": "oklch(43.2% 0.232 292.759)",
	"colors.violet.900": "oklch(38% 0.189 293.745)",
	"colors.violet.950": "oklch(28.3% 0.141 291.089)",
	"colors.warning.300": "var(--warning-300)",
	"colors.warning.500": "var(--warning-500)",
	"colors.warning.700": "var(--warning-700)",
	"colors.white": "#fff",
	"colors.yellow.100": "oklch(97.3% 0.071 103.193)",
	"colors.yellow.200": "oklch(94.5% 0.129 101.54)",
	"colors.yellow.300": "oklch(90.5% 0.182 98.111)",
	"colors.yellow.400": "oklch(85.2% 0.199 91.936)",
	"colors.yellow.50": "oklch(98.7% 0.026 102.212)",
	"colors.yellow.500": "oklch(79.5% 0.184 86.047)",
	"colors.yellow.600": "oklch(68.1% 0.162 75.834)",
	"colors.yellow.700": "oklch(55.4% 0.135 66.442)",
	"colors.yellow.800": "oklch(47.6% 0.114 61.907)",
	"colors.yellow.900": "oklch(42.1% 0.095 57.708)",
	"colors.yellow.950": "oklch(28.6% 0.066 53.813)",
	"colors.zinc.100": "oklch(96.7% 0.001 286.375)",
	"colors.zinc.200": "oklch(92% 0.004 286.32)",
	"colors.zinc.300": "oklch(87.1% 0.006 286.286)",
	"colors.zinc.400": "oklch(70.5% 0.015 286.067)",
	"colors.zinc.50": "oklch(98.5% 0 0)",
	"colors.zinc.500": "oklch(55.2% 0.016 285.938)",
	"colors.zinc.600": "oklch(44.2% 0.017 285.786)",
	"colors.zinc.700": "oklch(37% 0.013 285.805)",
	"colors.zinc.800": "oklch(27.4% 0.006 286.033)",
	"colors.zinc.900": "oklch(21% 0.006 285.885)",
	"colors.zinc.950": "oklch(14.1% 0.005 285.823)",
	"durations.fast": "150ms",
	"durations.faster": "100ms",
	"durations.fastest": "50ms",
	"durations.normal": "200ms",
	"durations.slow": "300ms",
	"durations.slower": "400ms",
	"durations.slowest": "500ms",
	"easings.default": "cubic-bezier(0.4, 0, 0.2, 1)",
	"easings.in": "cubic-bezier(0.4, 0, 1, 1)",
	"easings.in-out": "cubic-bezier(0.4, 0, 0.2, 1)",
	"easings.linear": "linear",
	"easings.out": "cubic-bezier(0, 0, 0.2, 1)",
	"fontSizes.2xl": "1.5rem",
	"fontSizes.2xs": "0.5rem",
	"fontSizes.3xl": "1.875rem",
	"fontSizes.4xl": "2.25rem",
	"fontSizes.5xl": "3rem",
	"fontSizes.6xl": "3.75rem",
	"fontSizes.7xl": "4.5rem",
	"fontSizes.8xl": "6rem",
	"fontSizes.9xl": "8rem",
	"fontSizes.lg": "1.125rem",
	"fontSizes.md": "1rem",
	"fontSizes.sm": "0.875rem",
	"fontSizes.xl": "1.25rem",
	"fontSizes.xs": "0.75rem",
	"fontWeights.black": "900",
	"fontWeights.bold": "700",
	"fontWeights.extrabold": "800",
	"fontWeights.extralight": "200",
	"fontWeights.light": "300",
	"fontWeights.medium": "500",
	"fontWeights.normal": "400",
	"fontWeights.semibold": "600",
	"fontWeights.thin": "100",
	"fonts.mono": "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
	"fonts.sans": "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"",
	"fonts.serif": "ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
	"letterSpacings.normal": "0em",
	"letterSpacings.tight": "-0.025em",
	"letterSpacings.tighter": "-0.05em",
	"letterSpacings.wide": "0.025em",
	"letterSpacings.wider": "0.05em",
	"letterSpacings.widest": "0.1em",
	"lineHeights.loose": "2",
	"lineHeights.none": "1",
	"lineHeights.normal": "1.5",
	"lineHeights.relaxed": "1.625",
	"lineHeights.snug": "1.375",
	"lineHeights.tight": "1.25",
	"radii.2xl": "1rem",
	"radii.3xl": "1.5rem",
	"radii.4xl": "2rem",
	"radii.full": "9999px",
	"radii.lg": "0.5rem",
	"radii.md": "0.375rem",
	"radii.sm": "0.25rem",
	"radii.xl": "0.75rem",
	"radii.xs": "0.125rem",
	"shadows.2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
	"shadows.2xs": "0 1px rgb(0 0 0 / 0.05)",
	"shadows.inner": "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
	"shadows.inset-2xs": "inset 0 1px rgb(0 0 0 / 0.05)",
	"shadows.inset-sm": "inset 0 2px 4px rgb(0 0 0 / 0.05)",
	"shadows.inset-xs": "inset 0 1px 1px rgb(0 0 0 / 0.05)",
	"shadows.lg": "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
	"shadows.md": "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
	"shadows.sm": "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
	"shadows.xl": "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
	"shadows.xs": "0 1px 2px 0 rgb(0 0 0 / 0.05)",
	"sizes.0": "0rem",
	"sizes.0.5": "0.125rem",
	"sizes.1": "0.25rem",
	"sizes.1.5": "0.375rem",
	"sizes.10": "2.5rem",
	"sizes.11": "2.75rem",
	"sizes.12": "3rem",
	"sizes.14": "3.5rem",
	"sizes.16": "4rem",
	"sizes.2": "0.5rem",
	"sizes.2.5": "0.625rem",
	"sizes.20": "5rem",
	"sizes.24": "6rem",
	"sizes.28": "7rem",
	"sizes.2xl": "42rem",
	"sizes.3": "0.75rem",
	"sizes.3.5": "0.875rem",
	"sizes.32": "8rem",
	"sizes.36": "9rem",
	"sizes.3xl": "48rem",
	"sizes.4": "1rem",
	"sizes.4.5": "1.125rem",
	"sizes.40": "10rem",
	"sizes.44": "11rem",
	"sizes.48": "12rem",
	"sizes.4xl": "56rem",
	"sizes.5": "1.25rem",
	"sizes.5.5": "1.375rem",
	"sizes.52": "13rem",
	"sizes.56": "14rem",
	"sizes.5xl": "64rem",
	"sizes.6": "1.5rem",
	"sizes.60": "15rem",
	"sizes.64": "16rem",
	"sizes.6xl": "72rem",
	"sizes.7": "1.75rem",
	"sizes.72": "18rem",
	"sizes.7xl": "80rem",
	"sizes.8": "2rem",
	"sizes.80": "20rem",
	"sizes.8xl": "90rem",
	"sizes.9": "2.25rem",
	"sizes.96": "24rem",
	"sizes.breakpoint-2xl": "96rem",
	"sizes.breakpoint-desktop": "1024px",
	"sizes.breakpoint-lg": "64rem",
	"sizes.breakpoint-md": "48rem",
	"sizes.breakpoint-sm": "40rem",
	"sizes.breakpoint-tablet": "768px",
	"sizes.breakpoint-xl": "80rem",
	"sizes.breakpoint-xs": "300px",
	"sizes.container": "var(--container-size)",
	"sizes.fit": "fit-content",
	"sizes.full": "100%",
	"sizes.lg": "32rem",
	"sizes.max": "max-content",
	"sizes.md": "28rem",
	"sizes.min": "min-content",
	"sizes.narrowContainer": "var(--narrow-container-size)",
	"sizes.prose": "60ch",
	"sizes.sm": "24rem",
	"sizes.xl": "36rem",
	"sizes.xs": "20rem",
	"spacing.-0.5": "calc(var(--gEfgCY) * -1)",
	"spacing.-1": "calc(var(--dacYEG) * -1)",
	"spacing.-1.5": "calc(var(--jJIQbV) * -1)",
	"spacing.-10": "calc(var(--ieMJEa) * -1)",
	"spacing.-11": "calc(var(--cBFxYr) * -1)",
	"spacing.-12": "calc(var(--uyqLg) * -1)",
	"spacing.-14": "calc(var(--hXmVPi) * -1)",
	"spacing.-16": "calc(var(--mYCWo) * -1)",
	"spacing.-2": "calc(var(--ebuyxV) * -1)",
	"spacing.-2.5": "calc(var(--cqwGG) * -1)",
	"spacing.-20": "calc(var(--jgejxp) * -1)",
	"spacing.-24": "calc(var(--iYEvIx) * -1)",
	"spacing.-28": "calc(var(--dzpWeZ) * -1)",
	"spacing.-3": "calc(var(--bYDCFo) * -1)",
	"spacing.-3.5": "calc(var(--dhUgfD) * -1)",
	"spacing.-32": "calc(var(--fifqnK) * -1)",
	"spacing.-36": "calc(var(--faFCyS) * -1)",
	"spacing.-4": "calc(var(--hIimNX) * -1)",
	"spacing.-4.5": "calc(var(--jjtyTI) * -1)",
	"spacing.-40": "calc(var(--bxwoov) * -1)",
	"spacing.-44": "calc(var(--huPzrf) * -1)",
	"spacing.-48": "calc(var(--hgdKvb) * -1)",
	"spacing.-5": "calc(var(--fFrqVq) * -1)",
	"spacing.-5.5": "calc(var(--ZByTJ) * -1)",
	"spacing.-52": "calc(var(--cZMJbQ) * -1)",
	"spacing.-56": "calc(var(--iXfUeA) * -1)",
	"spacing.-6": "calc(var(--gGIQOF) * -1)",
	"spacing.-60": "calc(var(--vWSpd) * -1)",
	"spacing.-64": "calc(var(--gtqdrN) * -1)",
	"spacing.-7": "calc(var(--eDRUVY) * -1)",
	"spacing.-72": "calc(var(--hNtIEu) * -1)",
	"spacing.-8": "calc(var(--flZesP) * -1)",
	"spacing.-80": "calc(var(--kqIPsj) * -1)",
	"spacing.-9": "calc(var(--djiiAi) * -1)",
	"spacing.-96": "calc(var(--wdMRQ) * -1)",
	"spacing.0": "0rem",
	"spacing.0.5": "0.125rem",
	"spacing.1": "0.25rem",
	"spacing.1.5": "0.375rem",
	"spacing.10": "2.5rem",
	"spacing.11": "2.75rem",
	"spacing.12": "3rem",
	"spacing.14": "3.5rem",
	"spacing.16": "4rem",
	"spacing.2": "0.5rem",
	"spacing.2.5": "0.625rem",
	"spacing.20": "5rem",
	"spacing.24": "6rem",
	"spacing.28": "7rem",
	"spacing.3": "0.75rem",
	"spacing.3.5": "0.875rem",
	"spacing.32": "8rem",
	"spacing.36": "9rem",
	"spacing.4": "1rem",
	"spacing.4.5": "1.125rem",
	"spacing.40": "10rem",
	"spacing.44": "11rem",
	"spacing.48": "12rem",
	"spacing.5": "1.25rem",
	"spacing.5.5": "1.375rem",
	"spacing.52": "13rem",
	"spacing.56": "14rem",
	"spacing.6": "1.5rem",
	"spacing.60": "15rem",
	"spacing.64": "16rem",
	"spacing.7": "1.75rem",
	"spacing.72": "18rem",
	"spacing.8": "2rem",
	"spacing.80": "20rem",
	"spacing.9": "2.25rem",
	"spacing.96": "24rem"
};
var tokenVars = {
	"sizes.0.5": "var(--hghuKL)",
	"sizes.1.5": "var(--drtIxO)",
	"sizes.2.5": "var(--siblR)",
	"sizes.3.5": "var(--hSPYxQ)",
	"sizes.4.5": "var(--fHQhyH)",
	"sizes.5.5": "var(--bTcvlK)",
	"spacing.-0.5": "var(--gEfgCY)",
	"spacing.-1": "var(--dacYEG)",
	"spacing.-1.5": "var(--jJIQbV)",
	"spacing.-10": "var(--ieMJEa)",
	"spacing.-11": "var(--cBFxYr)",
	"spacing.-12": "var(--uyqLg)",
	"spacing.-14": "var(--hXmVPi)",
	"spacing.-16": "var(--mYCWo)",
	"spacing.-2": "var(--ebuyxV)",
	"spacing.-2.5": "var(--cqwGG)",
	"spacing.-20": "var(--jgejxp)",
	"spacing.-24": "var(--iYEvIx)",
	"spacing.-28": "var(--dzpWeZ)",
	"spacing.-3": "var(--bYDCFo)",
	"spacing.-3.5": "var(--dhUgfD)",
	"spacing.-32": "var(--fifqnK)",
	"spacing.-36": "var(--faFCyS)",
	"spacing.-4": "var(--hIimNX)",
	"spacing.-4.5": "var(--jjtyTI)",
	"spacing.-40": "var(--bxwoov)",
	"spacing.-44": "var(--huPzrf)",
	"spacing.-48": "var(--hgdKvb)",
	"spacing.-5": "var(--fFrqVq)",
	"spacing.-5.5": "var(--ZByTJ)",
	"spacing.-52": "var(--cZMJbQ)",
	"spacing.-56": "var(--iXfUeA)",
	"spacing.-6": "var(--gGIQOF)",
	"spacing.-60": "var(--vWSpd)",
	"spacing.-64": "var(--gtqdrN)",
	"spacing.-7": "var(--eDRUVY)",
	"spacing.-72": "var(--hNtIEu)",
	"spacing.-8": "var(--flZesP)",
	"spacing.-80": "var(--kqIPsj)",
	"spacing.-9": "var(--djiiAi)",
	"spacing.-96": "var(--wdMRQ)",
	"spacing.0.5": "var(--gEfgCY)",
	"spacing.1.5": "var(--jJIQbV)",
	"spacing.2.5": "var(--cqwGG)",
	"spacing.3.5": "var(--dhUgfD)",
	"spacing.4.5": "var(--jjtyTI)",
	"spacing.5.5": "var(--ZByTJ)"
};
var resolveVar = (path) => tokenVars[path] || toCssVar(path);
var token = /* @__PURE__ */ Object.assign(function token(path, fallback) {
	const value = tokens[path];
	return value === void 0 ? colorMix(tokens, path, resolveVar) || fallback : value || resolveVar(path);
}, { var: function tokenVar(path, fallback) {
	return tokens[path] === void 0 ? fallback : resolveVar(path);
} });
//#endregion
//#region src/menu-section-context.tsx
var context$3 = createContext({
	viewport: "desktop",
	section: "",
	area: ""
});
var MenuSectionContextProvider = context$3.Provider;
var useMenuSection = () => {
	return useContext(context$3);
};
//#endregion
//#region src/menu-context.tsx
var context$2 = createContext({
	refs: {
		top: { current: null },
		middle: { current: null },
		bottom: { current: null },
		mobileTop: { current: null },
		mobileBottom: { current: null }
	},
	scrollingUp: true,
	highlight: /* @__PURE__ */ new Set(),
	addHighlight: () => {},
	removeHighlight: () => {},
	has: {
		top: true,
		middle: true,
		bottom: true,
		mobileTop: true,
		mobileBottom: true
	}
});
var MenuContextProvider = context$2.Provider;
var useMenu = () => {
	return useContext(context$2);
};
//#endregion
//#region src/menu-section.tsx
function MenuSection({ containerRef, backgroundColor = "#fff", darkBackground = false, sticky = false, scrollingUp = false, elevated = false, offset = 0, offsetScrollingUp = 0, zIndex = 1e3, isDesktop = true, children, css: cssFromProp }) {
	const baseStyle = useMemo(() => ({
		backgroundColor: backgroundColor ?? "#fff",
		zIndex,
		position: "sticky",
		top: `${offsetScrollingUp.toString()}px`
	}), [
		backgroundColor,
		offsetScrollingUp,
		zIndex
	]);
	const animationSteps = useMemo(() => {
		const defaultTranstion = "top .2s ease, box-shadow .2s ease";
		const disableTranstion = "box-shadow .2s ease";
		if (sticky) {
			if (scrollingUp) return [{
				delay: 0,
				style: {
					transition: defaultTranstion,
					position: "sticky",
					top: `${offsetScrollingUp.toString()}px`
				}
			}];
			return [{
				delay: 200,
				style: {
					transition: defaultTranstion,
					position: "sticky",
					top: `${offset.toString()}px`
				}
			}];
		}
		if (scrollingUp) return [{
			delay: 0,
			style: {
				transition: disableTranstion,
				position: "sticky",
				top: `${offset.toString()}px`
			}
		}, {
			delay: 200,
			style: {
				transition: defaultTranstion,
				position: "sticky",
				top: `${offsetScrollingUp.toString()}px`
			}
		}];
		if (window.scrollY > offsetScrollingUp + (containerRef.current?.getBoundingClientRect().height ?? 0) - offset) return [{
			delay: 0,
			style: {
				transition: disableTranstion,
				position: "relative",
				top: "0px"
			}
		}, {
			delay: 200,
			style: {
				transition: defaultTranstion,
				position: "sticky",
				top: `${(offset - (containerRef.current?.getBoundingClientRect().height ?? 0)).toString()}px`
			}
		}];
		return [{
			delay: 200,
			style: {
				transition: defaultTranstion,
				position: "sticky",
				top: `${(offset - (containerRef.current?.getBoundingClientRect().height ?? 0)).toString()}px`
			}
		}, {
			delay: 0,
			style: {
				transition: disableTranstion,
				position: "relative",
				top: `0px`
			}
		}];
	}, [
		containerRef,
		offset,
		offsetScrollingUp,
		scrollingUp,
		sticky
	]);
	const animationOpts = useMemo(() => ({
		baseStyle,
		steps: animationSteps
	}), [baseStyle, animationSteps]);
	const style = useAnimation(animationOpts);
	return /* @__PURE__ */ jsx("div", {
		style,
		className: cx(isDesktop ? "menu-section-desktop" : "menu-section-mobile", css({
			w: "full",
			flexDir: "column",
			alignItems: "center",
			justifyContent: "center",
			shadow: elevated ? "4px 8px 8px 0 rgb(0 0 0 / 0.025), -4px 8px 8px 0 rgb(0 0 0 / 0.025)" : "none"
		})),
		ref: containerRef,
		children: /* @__PURE__ */ jsx("div", {
			className: css({
				w: "full",
				display: "flex",
				flexDir: "row",
				alignItems: "center",
				justifyContent: "space-between",
				maxWidth: "container",
				minH: "12"
			}, cssFromProp),
			children: /* @__PURE__ */ jsx(ThemeProvider, {
				theme: darkBackground ? "dark" : "light",
				children
			})
		})
	});
}
//#endregion
//#region src/menu-manager-context.tsx
var context$1 = createContext({
	offset: 0,
	setOffset: () => {},
	height: 0,
	setHeight: () => {},
	scrollingUpHeight: 0,
	setScrollingUpHeight: () => {},
	scrollingDownHeight: 0,
	setScrollingDownHeight: () => {}
});
function MenuManagerContextProvider({ children }) {
	const [offset, setOffset] = useState(0);
	const [height, setHeight] = useState(0);
	const [scrollingUpHeight, setScrollingUpHeight] = useState(0);
	const [scrollingDownHeight, setScrollingDownHeight] = useState(0);
	const value = useMemo(() => ({
		offset,
		setOffset,
		height,
		setHeight,
		scrollingUpHeight,
		setScrollingUpHeight,
		scrollingDownHeight,
		setScrollingDownHeight
	}), [
		offset,
		height,
		scrollingUpHeight,
		scrollingDownHeight
	]);
	return /* @__PURE__ */ jsx(context$1.Provider, {
		value,
		children
	});
}
var useMenuManagerContext = () => {
	return useContext(context$1);
};
var useMenuHeight = () => {
	const c = useMenuManagerContext();
	const { route: { layoutOptions: { hideMenu } } } = usePage();
	if (hideMenu) return 0;
	return c.height;
};
var useMenuOffset = () => {
	return useMenuManagerContext().offset;
};
//#endregion
//#region src/desktop/sections/menu-top-section.tsx
function MenuTopSection({ containerRef }) {
	const { menuDesktop: { top, middle, bottom } } = usePage();
	const { has, scrollingUp } = useMenu();
	const offset = useMenuOffset();
	if (!has.top) return null;
	return /* @__PURE__ */ jsxs(MenuSection, {
		containerRef,
		backgroundColor: top.color,
		darkBackground: top.dark,
		sticky: top.sticky,
		offset,
		offsetScrollingUp: offset,
		scrollingUp,
		zIndex: zIndexes.menu + 2,
		elevated: (Boolean(top.sticky) || scrollingUp) && (!has.middle || !middle.sticky && !scrollingUp) ? !has.bottom || !bottom.sticky && !scrollingUp : null,
		css: css.raw({
			py: "2",
			px: "4"
		}),
		children: [/* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "desktop",
				section: "top",
				area: "left"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "start",
					gap: "4"
				}),
				children: map(top.left ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		}), /* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "desktop",
				section: "top",
				area: "right"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "end",
					gap: "4"
				}),
				children: map(top.right ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		})]
	});
}
//#endregion
//#region src/desktop/sections/menu-middle-section.tsx
function MenuMiddleSection({ containerRef }) {
	const { menuDesktop: { top, middle, bottom } } = usePage();
	const { refs, has, scrollingUp } = useMenu();
	const offset = useMenuOffset();
	if (!has.middle) return null;
	return /* @__PURE__ */ jsxs(MenuSection, {
		containerRef,
		backgroundColor: middle.color,
		darkBackground: middle.dark,
		sticky: middle.sticky,
		offset: offset + (top.sticky ? Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0) : 0),
		offsetScrollingUp: offset + Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0),
		scrollingUp,
		zIndex: zIndexes.menu + 1,
		elevated: Boolean(middle.sticky) || scrollingUp ? !has.bottom || !bottom.sticky && !scrollingUp : null,
		css: css.raw({
			py: "4",
			px: "4"
		}),
		children: [
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "middle",
					area: "left"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "start",
						gap: "2"
					}),
					children: map(middle.left ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			}),
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "middle",
					area: "center"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "center",
						gap: "2"
					}),
					children: map(middle.center ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			}),
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "middle",
					area: "right"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "end",
						gap: "2"
					}),
					children: map(middle.right ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			})
		]
	});
}
//#endregion
//#region src/desktop/sections/menu-bottom-section.tsx
function MenuBottomSection({ containerRef }) {
	const { menuDesktop: { top, middle, bottom } } = usePage();
	const { refs, has, scrollingUp } = useMenu();
	const offset = useMenuOffset();
	if (!has.bottom) return null;
	return /* @__PURE__ */ jsxs(MenuSection, {
		containerRef,
		backgroundColor: bottom.color,
		darkBackground: bottom.dark,
		sticky: bottom.sticky,
		offset: offset + (top.sticky ? Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0) : 0) + (middle.sticky ? Math.floor(refs.middle.current?.getBoundingClientRect().height ?? 0) : 0),
		offsetScrollingUp: offset + Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0) + Math.floor(refs.middle.current?.getBoundingClientRect().height ?? 0),
		scrollingUp,
		zIndex: zIndexes.menu,
		elevated: Boolean(bottom.sticky) || scrollingUp,
		css: css.raw({
			py: "4",
			px: "4"
		}),
		children: [
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "bottom",
					area: "left"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "start",
						gap: "2"
					}),
					children: map(bottom.left ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			}),
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "bottom",
					area: "center"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "center",
						gap: "2"
					}),
					children: map(bottom.center ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			}),
			/* @__PURE__ */ jsx(MenuSectionContextProvider, {
				value: {
					viewport: "desktop",
					section: "bottom",
					area: "right"
				},
				children: /* @__PURE__ */ jsx("div", {
					className: css({
						display: "flex",
						flexDir: "row",
						alignItems: "center",
						justifyContent: "end",
						gap: "2"
					}),
					children: map(bottom.right ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
				})
			})
		]
	});
}
//#endregion
//#region src/mobile/sections/menu-mobile-top-section.tsx
function MenuMobileTopSection({ containerRef }) {
	const { menuMobile: { external: { top, bottom } } } = usePage();
	const { has, scrollingUp } = useMenu();
	const offset = useMenuOffset();
	if (!has.mobileTop) return null;
	return /* @__PURE__ */ jsxs(MenuSection, {
		isDesktop: false,
		containerRef,
		backgroundColor: top.color,
		darkBackground: top.dark,
		sticky: top.sticky,
		offset,
		offsetScrollingUp: offset,
		scrollingUp,
		zIndex: zIndexes.menu + 2,
		elevated: Boolean(top.sticky) || scrollingUp ? !has.mobileBottom || !bottom.sticky && !scrollingUp : null,
		css: css.raw({
			py: "1",
			px: "4",
			minH: "10"
		}),
		children: [/* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "mobile",
				section: "external-top",
				area: "left"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "start",
					gap: "2"
				}),
				children: map(top.left ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		}), /* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "mobile",
				section: "external-top",
				area: "right"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "end",
					gap: "2"
				}),
				children: map(top.right ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		})]
	});
}
//#endregion
//#region src/mobile/sections/menu-mobile-bottom-section.tsx
function MenuMobileBottomSection({ containerRef }) {
	const { menuMobile: { external: { top, bottom } } } = usePage();
	const { refs, has, scrollingUp } = useMenu();
	const offset = useMenuOffset();
	if (!has.mobileBottom) return null;
	return /* @__PURE__ */ jsxs(MenuSection, {
		isDesktop: false,
		containerRef,
		backgroundColor: bottom.color,
		darkBackground: bottom.dark,
		sticky: bottom.sticky,
		offset: offset + (top.sticky ? Math.floor(refs.mobileTop.current?.getBoundingClientRect().height ?? 0) : 0),
		offsetScrollingUp: offset + Math.floor(refs.mobileTop.current?.getBoundingClientRect().height ?? 0),
		scrollingUp,
		zIndex: zIndexes.menu + 1,
		elevated: Boolean(bottom.sticky) || scrollingUp,
		css: css.raw({
			py: "4",
			px: "4"
		}),
		children: [/* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "mobile",
				section: "external-bottom",
				area: "left"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "start",
					gap: "2"
				}),
				children: map(bottom.left ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		}), /* @__PURE__ */ jsx(MenuSectionContextProvider, {
			value: {
				viewport: "mobile",
				section: "external-bottom",
				area: "right"
			},
			children: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDir: "row",
					alignItems: "center",
					justifyContent: "end",
					gap: "2"
				}),
				children: map(bottom.right ?? [], (c) => /* @__PURE__ */ jsx(Content, { ...c }, c.uuid))
			})
		})]
	});
}
//#endregion
//#region src/menu.tsx
var desktopBreakpoint = parseInt(token("breakpoints.desktop", "1024px"));
function MenuSize() {
	const { offset, setHeight, setScrollingUpHeight, setScrollingDownHeight } = useMenuManagerContext();
	const { refs, scrollingUp, has } = useMenu();
	const { menuDesktop: { top, middle, bottom }, menuMobile: { external } } = usePage();
	const [isDesktop, setIsDesktop] = useState(true);
	useDebouncedResizeHandler(100, useCallback((w) => {
		setIsDesktop(w >= desktopBreakpoint);
	}, []));
	const recompute = useCallback(() => {
		if (isDesktop) {
			const scrollingUpHeight = offset + (has.top ? Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0) : 0) + (has.middle ? Math.floor(refs.middle.current?.getBoundingClientRect().height ?? 0) : 0) + (has.bottom ? Math.floor(refs.bottom.current?.getBoundingClientRect().height ?? 0) : 0);
			const scrollingDownHeight = offset + (has.top && top.sticky ? Math.floor(refs.top.current?.getBoundingClientRect().height ?? 0) : 0) + (has.middle && middle.sticky ? Math.floor(refs.middle.current?.getBoundingClientRect().height ?? 0) : 0) + (has.bottom && bottom.sticky ? Math.floor(refs.bottom.current?.getBoundingClientRect().height ?? 0) : 0);
			if (scrollingUp) setHeight(scrollingUpHeight);
			else setHeight(scrollingDownHeight);
			setScrollingUpHeight(scrollingUpHeight);
			setScrollingDownHeight(scrollingDownHeight);
		} else {
			const scrollingUpHeight = offset + (has.mobileTop ? Math.floor(refs.mobileTop.current?.getBoundingClientRect().height ?? 0) : 0) + (has.mobileBottom ? Math.floor(refs.mobileBottom.current?.getBoundingClientRect().height ?? 0) : 0);
			const scrollingDownHeight = offset + (has.mobileTop && external.top.sticky ? Math.floor(refs.mobileTop.current?.getBoundingClientRect().height ?? 0) : 0) + (has.mobileBottom && external.bottom.sticky ? Math.floor(refs.mobileBottom.current?.getBoundingClientRect().height ?? 0) : 0);
			if (scrollingUp) setHeight(scrollingUpHeight);
			else setHeight(scrollingDownHeight);
			setScrollingUpHeight(scrollingUpHeight);
			setScrollingDownHeight(scrollingDownHeight);
		}
	}, [
		bottom.sticky,
		external.bottom.sticky,
		external.top.sticky,
		has.bottom,
		has.middle,
		has.mobileBottom,
		has.mobileTop,
		has.top,
		isDesktop,
		middle.sticky,
		offset,
		refs,
		scrollingUp,
		setHeight,
		setScrollingDownHeight,
		setScrollingUpHeight,
		top.sticky
	]);
	useEffect(() => {
		recompute();
	}, [recompute]);
	useEffect(() => {
		if (typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(() => {
			recompute();
		});
		[
			refs.top.current,
			refs.middle.current,
			refs.bottom.current,
			refs.mobileTop.current,
			refs.mobileBottom.current
		].forEach((element) => {
			if (element) observer.observe(element);
		});
		return () => {
			observer.disconnect();
		};
	}, [recompute, refs]);
	return null;
}
function InnerMenu() {
	const { menuDesktop, menuMobile } = usePage();
	const topRef = useRef(null);
	const middleRef = useRef(null);
	const bottomRef = useRef(null);
	const mobileTopRef = useRef(null);
	const mobileBottomRef = useRef(null);
	const [scrollingUp, setScrollingUp] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);
	const controlNavbar = useCallback(() => {
		setScrollingUp(window.scrollY < lastScrollY);
		setLastScrollY(window.scrollY);
	}, [lastScrollY]);
	useEffect(() => {
		window.addEventListener("scroll", controlNavbar);
		return () => {
			window.removeEventListener("scroll", controlNavbar);
		};
	}, [controlNavbar]);
	const has = useMemo(() => ({
		top: !isEmpty(menuDesktop.top.left) || !isEmpty(menuDesktop.top.right),
		middle: !isEmpty(menuDesktop.middle.left) || !isEmpty(menuDesktop.middle.center) || !isEmpty(menuDesktop.middle.right),
		bottom: !isEmpty(menuDesktop.bottom.left) || !isEmpty(menuDesktop.bottom.center) || !isEmpty(menuDesktop.bottom.right),
		mobileTop: !isEmpty(menuMobile.external.top.left) || !isEmpty(menuMobile.external.top.right),
		mobileBottom: !isEmpty(menuMobile.external.bottom.left) || !isEmpty(menuMobile.external.bottom.right)
	}), [
		menuDesktop.bottom.center,
		menuDesktop.bottom.left,
		menuDesktop.bottom.right,
		menuDesktop.middle.center,
		menuDesktop.middle.left,
		menuDesktop.middle.right,
		menuDesktop.top.left,
		menuDesktop.top.right,
		menuMobile.external.bottom.left,
		menuMobile.external.bottom.right,
		menuMobile.external.top.left,
		menuMobile.external.top.right
	]);
	const [highlight, setHighlight] = useState(/* @__PURE__ */ new Set());
	const addHighlight = useCallback((h) => {
		setHighlight((p) => {
			const r = /* @__PURE__ */ new Set([...p]);
			r.add(h);
			return r;
		});
	}, []);
	const removeHighlight = useCallback((h) => {
		setHighlight((p) => {
			const r = /* @__PURE__ */ new Set([...p]);
			r.delete(h);
			return r;
		});
	}, []);
	const contextValue = useMemo(() => ({
		refs: {
			top: topRef,
			middle: middleRef,
			bottom: bottomRef,
			mobileTop: mobileTopRef,
			mobileBottom: mobileBottomRef
		},
		highlight,
		addHighlight,
		removeHighlight,
		scrollingUp,
		has
	}), [
		highlight,
		addHighlight,
		removeHighlight,
		scrollingUp,
		has
	]);
	const [showShadowOverlay, setShowShadowOverlay] = useState(highlight.size > 0);
	useEffect(() => {
		let handler;
		let handlerDone = false;
		if (highlight.size > 0) setShowShadowOverlay(true);
		else handler = setTimeout(() => {
			setShowShadowOverlay(false);
			handlerDone = true;
		}, 50);
		return () => {
			if (handler) {
				clearTimeout(handler);
				if (!handlerDone) setShowShadowOverlay(false);
			}
		};
	}, [highlight.size]);
	const animationSteps = useMemo(() => {
		if (showShadowOverlay) return [{
			delay: 1,
			style: {
				opacity: 0,
				transition: "none",
				display: "block"
			}
		}, {
			delay: 200,
			style: {
				opacity: 1,
				transition: "opacity .2s ease-out",
				display: "block"
			}
		}];
		return [
			{
				delay: 1,
				style: {
					opacity: 1,
					transition: "none",
					display: "block"
				}
			},
			{
				delay: 150,
				style: {
					opacity: 0,
					transition: "opacity .15s ease-in",
					display: "block"
				}
			},
			{
				delay: 1,
				style: { display: "none" }
			}
		];
	}, [showShadowOverlay]);
	const animationOpts = useMemo(() => ({
		baseStyle: showShadowOverlay ? {
			display: "block",
			opacity: 1,
			animation: "unset",
			zIndex: zIndexes.menu - 1
		} : {
			display: "none",
			opacity: 0,
			animation: "unset",
			zIndex: zIndexes.menu - 1
		},
		steps: animationSteps
	}), [animationSteps, showShadowOverlay]);
	const shadowOverlayStyle = useAnimation(animationOpts);
	const hideShadowOverlay = useCallback(() => {
		setShowShadowOverlay(false);
	}, []);
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(Style, { style: "core-menu" }),
		/* @__PURE__ */ jsx(Overlay, { children: /* @__PURE__ */ jsx("div", {
			style: shadowOverlayStyle,
			onMouseEnter: hideShadowOverlay,
			onMouseMove: hideShadowOverlay,
			onClick: hideShadowOverlay,
			onKeyDown: hideShadowOverlay,
			tabIndex: -1,
			role: "button",
			className: css({
				position: "fixed",
				top: 0,
				left: 0,
				width: "full",
				height: "full",
				animation: "menuShadowFadeIn .2s ease-out 0s 1 both",
				backgroundColor: "black/30",
				display: "none",
				opacity: 0,
				zIndex: zIndexes.menu - 1
			})
		}) }),
		/* @__PURE__ */ jsxs(MenuContextProvider, {
			value: contextValue,
			children: [
				/* @__PURE__ */ jsx(MenuTopSection, { containerRef: topRef }),
				/* @__PURE__ */ jsx(MenuMiddleSection, { containerRef: middleRef }),
				/* @__PURE__ */ jsx(MenuBottomSection, { containerRef: bottomRef }),
				/* @__PURE__ */ jsx(MenuMobileTopSection, { containerRef: mobileTopRef }),
				/* @__PURE__ */ jsx(MenuMobileBottomSection, { containerRef: mobileBottomRef }),
				/* @__PURE__ */ jsx(MenuSize, {})
			]
		})
	] });
}
function Menu() {
	const { route: { layoutOptions: { hideMenu } } } = usePage();
	if (hideMenu) return null;
	return /* @__PURE__ */ jsx(InnerMenu, {});
}
//#endregion
//#region src/menu-overlay-context.tsx
var context = createContext({
	onClose: () => {},
	pages: [],
	addPage: () => {
		return () => {};
	},
	removePage: () => {},
	clearPages: () => {}
});
function MenuOverlayContextProvider({ children, onClose }) {
	const [pages, setPages] = useState([]);
	const addPage = useCallback((id, page) => {
		setPages((oldPages) => {
			if (some(oldPages, (p) => p.id === id)) return oldPages;
			return [...oldPages, {
				page,
				id
			}];
		});
		return () => {
			setPages((oldPages) => {
				return filter(oldPages, (p) => p.id !== id);
			});
		};
	}, []);
	const removePage = useCallback((id) => {
		setPages((oldPages) => {
			return filter(oldPages, (p) => p.id !== id);
		});
	}, []);
	const clearPages = useCallback(() => {
		setPages([]);
	}, []);
	const innerOnClose = useCallback(() => {
		onClose();
		setTimeout(() => {
			clearPages();
		}, 200);
	}, [onClose, clearPages]);
	const addRouterEventListener = useAddRouterEventListener();
	useEffect(() => {
		const remove = addRouterEventListener("navigate", () => {
			clearPages();
		});
		return () => {
			remove();
		};
	}, [addRouterEventListener, clearPages]);
	const value = useMemo(() => ({
		onClose: innerOnClose,
		pages,
		addPage,
		removePage,
		clearPages
	}), [
		innerOnClose,
		pages,
		addPage,
		removePage,
		clearPages
	]);
	return /* @__PURE__ */ jsx(context.Provider, {
		value,
		children
	});
}
var useMenuOverlay = () => {
	return useContext(context);
};
//#endregion
//#region src/mobile/menu-mobile-internal-overlay.tsx
function MobileInternalOverlayContent({ children, dark }) {
	const { pages, onClose, removePage } = useMenuOverlay();
	const currentPage = useMemo(() => last(pages), [pages]);
	const onBack = useCallback(() => {
		if (!currentPage) return;
		removePage(currentPage.id);
	}, [currentPage, removePage]);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
		className: css({
			display: "flex",
			flexDir: "column",
			alignItems: "end",
			justifyContent: "center"
		}),
		children: /* @__PURE__ */ jsx(Link, {
			title: "Close",
			onClick: onClose,
			css: css.raw({
				p: "2",
				lineHeight: "0",
				transition: "background-color .25s ease",
				_hover: { backgroundColor: dark ? "white/5" : "black/5" }
			}),
			children: /* @__PURE__ */ jsx(FontAwesome, {
				name: "fa-solid fa-xmark",
				size: 2,
				css: css.raw({
					color: dark ? "white" : "text.200",
					opacity: dark ? .5 : 1
				})
			})
		})
	}), currentPage ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Link, {
		title: currentPage.page.title,
		onClick: onBack,
		css: css.raw({
			color: dark ? "white" : "text.500",
			px: "6",
			py: "5",
			cursor: "pointer",
			textDecoration: "none",
			display: "flex",
			flexDir: "row",
			alignItems: "center",
			justifyContent: "start",
			textAlign: "left",
			transition: "background-color .25s ease",
			textWrap: "balance",
			borderBottom: "2",
			borderStyle: "solid",
			borderColor: dark ? "white/5" : "black/5",
			_hover: { backgroundColor: dark ? "white/10" : "black/10" }
		}),
		children: /* @__PURE__ */ jsxs("div", {
			className: css({
				position: "relative",
				display: "flex",
				flexDir: "row",
				alignItems: "center",
				justifyContent: "start",
				textAlign: "left"
			}),
			children: [/* @__PURE__ */ jsx(FontAwesome, {
				size: 1.5,
				css: css.raw({ mr: "2" }),
				name: "fa-solid fa-chevron-left"
			}), currentPage.page.title]
		})
	}), currentPage.page.children] }) : children] });
}
function MenuMobileInternalOverlay({ uuid, open, backgroundColor, dark, onClose, children, side }) {
	const offset = useMenuOffset();
	const overlayRef = useRef(null);
	useClickOutside(overlayRef, () => {
		onClose();
	});
	const { addHighlight, removeHighlight } = useMenu();
	useEffect(() => {
		if (open) addHighlight(uuid ?? "menu-mobile-internal-overlay");
		else removeHighlight(uuid ?? "menu-mobile-internal-overlay");
		return () => {
			if (open) removeHighlight(uuid ?? "menu-mobile-internal-overlay");
			else addHighlight(uuid ?? "menu-mobile-internal-overlay");
		};
	}, [
		addHighlight,
		open,
		removeHighlight,
		uuid
	]);
	useBodyScrollLock(open);
	const baseStyle = useMemo(() => ({
		top: `${offset.toString()}px`,
		zIndex: zIndexes.menuMobileOverlay,
		backgroundColor: backgroundColor ?? "#ffffff",
		...open ? {
			transition: "none",
			display: "flex",
			translate: "0 0"
		} : {
			transition: "none",
			display: "none",
			translate: side === "left" ? "-100% 0" : "100% 0"
		}
	}), [
		backgroundColor,
		offset,
		open,
		side
	]);
	const animationSteps = useMemo(() => {
		if (open) return [{
			delay: 1,
			style: {
				transition: "none",
				translate: side === "left" ? "-100% 0" : "100% 0"
			}
		}, {
			delay: 200,
			style: {
				transition: "translate 0.2s ease",
				translate: "0 0"
			}
		}];
		return [
			{
				delay: 1,
				style: {
					display: "flex",
					transition: "none",
					translate: "0 0"
				}
			},
			{
				delay: 200,
				style: {
					display: "flex",
					transition: "translate 0.2s ease",
					translate: side === "left" ? "-100% 0" : "100% 0"
				}
			},
			{
				delay: 1,
				style: { display: "none" }
			}
		];
	}, [open, side]);
	const style = useAnimation(useMemo(() => ({
		baseStyle,
		steps: animationSteps
	}), [baseStyle, animationSteps]));
	const addRouterEventListener = useAddRouterEventListener();
	useEffect(() => {
		const remove = addRouterEventListener("navigate", () => {
			onClose();
		});
		return () => {
			remove();
		};
	}, [addRouterEventListener, onClose]);
	return /* @__PURE__ */ jsx(Overlay, {
		clickable: true,
		children: /* @__PURE__ */ jsx(ThemeProvider, {
			theme: dark ? "dark" : "light",
			children: /* @__PURE__ */ jsx(MenuOverlayContextProvider, {
				onClose,
				children: /* @__PURE__ */ jsx("div", {
					ref: overlayRef,
					style,
					className: cx("menu-section-mobile", css({
						position: "fixed",
						left: 0,
						width: "full",
						height: "full",
						shadow: "md",
						display: open ? "flex" : "none",
						flexDir: "column",
						alignItems: "stretch",
						justifyContent: "start",
						overflow: "hidden"
					})),
					children: /* @__PURE__ */ jsx(MobileInternalOverlayContent, {
						dark,
						children
					})
				})
			})
		})
	});
}
//#endregion
export { Menu, MenuManagerContextProvider, MenuMobileInternalOverlay, MenuOverlayContextProvider, MenuSectionContextProvider, useMenu, useMenuHeight, useMenuManagerContext, useMenuOffset, useMenuOverlay, useMenuSection };
